<template>
  <q-table
    title="User List"
    :columns="columnDefs"
    :rows="rowData"
    row-key="email"
    :filter="filter"
    :filter-method="customFilter"
    :loading="loading"
    :pagination="pagination"
    :sort-method="CommonAdminHelper.customSort"
    virtual-scroll
    wrap-cells
    class="tableClass"
  >
    <template v-slot:top>
      <q-input
        outlined
        dense
        style="width: 10%"
        label="Filter Users"
        debounce="300"
        v-model="filter"
        :disable="noScopeFilter"
      />
      <q-toggle
        v-model="noScopeFilter"
        @update:model-value="toggleNoScopeFilter"
        label="No Scope Users"
        style="padding-left: 20px"
      />
      <q-space />
      <q-btn
        color="primary"
        unelevated
        :disable="loading"
        label="Add User"
        no-caps
        @click="addRow()"
        icon="add"
      />
    </template>
    <template v-slot:body-cell-actions="props">
      <q-td :props="props">
        <div style="display: flex; align-items: center; justify-content: center;">
          <q-btn
            dense
            round
            flat
            :color="!darkMode ? 'black' : 'white'"
            @click="editRow(props)"
            icon="edit"
            title="Edit"
            aria-label="Edit"
          />
          <q-btn
            dense
            round
            flat
            color="red"
            @click="deleteRow(props)"
            icon="delete"
            title="Delete"
            aria-label="Delete"
          />
        </div>
      </q-td>
    </template>
  </q-table>
</template>

<script>
import { computed, ref, watch } from "vue";
import axios from "axios";
import CommonAdminHelper from "../helper/CommonAdminHelper.js";
import NotificationHelper from "../helper/NotificationHelper";
import EmailService from "../service/EmailService.js";

export default {
  name: "UsersTable",
  props: {
    currentUserUID: String,
    darkMode: Boolean,
    userGroupsTable: Object,
    adminForm: Object,
    adminDelete: Object
  },
  emits: ["setAdminMode"],
  setup(props, { emit }) {
    const columnDefs = ref([
      { label: "Name", name: "name", field: "name", align: "center", sortable: true },
      { label: "Email", name: "email", field: "email", align: "center", sortable: true },
      { label: "Access", name: "access", field: "access", align: "center", sortable: true, format: (val) => { return val.replaceAll("_", " ") } },
      { label: "Scopes", name: "scopes", field: "scopes", align: "center", sortable: true },
      { label: "User Groups", name: "user_groups", field: "user_groups", align: "center", sortable: true, format: (val) => { return val.replaceAll(":::", ", ") } },
      { label: "Last Active", name: "last_active", field: "last_active", align: "center", sortable: true },
      { label: "Actions", name: "actions", field: "actions", align: "center" },
    ]);

    const responseData = ref();
    const rowData = ref([]);
    const filter = ref("");
    const loading = ref(false);
    const pagination = ref({ rowsPerPage: 10, sortBy: "name", ascending: true })
    const noScopeFilter = ref(false);

    const userGroupsDictionary = computed(() => {
      let returnValue = {};

      if (props.userGroupsTable !== undefined) {
        props.userGroupsTable.rowData.forEach((row) => {
          if (row.emails !== "") {
            row.emails.split(",").map((email) => {
              if (returnValue[email] === undefined) {
                returnValue[email] = [];
              }

              returnValue[email].push(row.name);
            })
          }
        })
      }

      return returnValue;
    });

    watch(
      () => props.currentUserUID,
      () => {
        if (props.currentUserUID !== null && props.currentUserUID !== undefined) {
          getUsers();
        }
      },
      { deep: true, immediate: true }
    );

    async function getUsers() {
      try {
        loading.value = true;

        const response = await axios.get(
          `${process.env.VUE_APP_API_GATEWAY}getAllUsers?currentUserUID=${props.currentUserUID}&key=${process.env.VUE_APP_API_KEY}`
        );

        responseData.value = response.data;
      } catch (error) {
        EmailService.sendErrorEmail("UsersTable.vue - getUsers", error);
        NotificationHelper.createErrorNotification("Error encountered. Please try again.");
        loading.value = false;
      }
    }

    watch([responseData, userGroupsDictionary], () => {
      if (responseData.value !== undefined) {
        let tempArray = [];

        if (props.currentUserUID !== process.env.VUE_APP_DEFAULT_USER_UID) {
          tempArray = responseData.value.filter((user) => {
            return user.email !== "facd-smtp-fwd@eworldes.com";
          });
        } else {
          tempArray = responseData.value;
        }

        tempArray = tempArray.map((user) => {
          return {
            name: user.displayName,
            email: user.email,
            access: CommonAdminHelper.getAlphabetizedString(user?.customClaims?.accessLevel),
            scopes: CommonAdminHelper.getAlphabetizedString(user?.customClaims?.scopesAccess),
            user_groups: userGroupsDictionary.value[user.email] !== undefined ? userGroupsDictionary.value[user.email].sort().join(":::") : "",
            last_active: CommonAdminHelper.convertTimeToHST(user.metadata.lastSignInTime),
            authenticationToken: user?.customClaims?.authenticationToken
          };
        });

        rowData.value = [...tempArray];
        loading.value = false;
      }
    });

    function toggleNoScopeFilter() {
      CommonAdminHelper.toggleNoScopeFilter(filter, noScopeFilter);
    }

    function customFilter(rows) {
      return CommonAdminHelper.customFilter(rows, filter, noScopeFilter);
    }

    function addRow() {
      emit("setAdminMode", "Add User");
      props.adminForm.addRow();
    }

    function editRow(prop) {
      emit("setAdminMode", "Update User");
      props.adminForm.editRow(prop);
    }

    function deleteRow(prop) {
      props.adminDelete.deleteRow(prop);
    }

    return {
      //helper
      CommonAdminHelper,

      //variable properties
      columnDefs,
      rowData,
      filter,
      loading,
      pagination,
      noScopeFilter,

      //functions
      getUsers,
      toggleNoScopeFilter,
      customFilter,
      addRow,
      editRow,
      deleteRow
    };
  },
};
</script>
